import { useEffect } from 'react';

export function Card({ children, className = '', ...props }) {
  return <section className={`card ${className}`} {...props}>{children}</section>;
}

export function Button({ children, variant = 'primary', ...props }) {
  return <button className={`button ${variant}`} {...props}>{children}</button>;
}

export function Field({ label, hint, children }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

export function Modal({ title, children, onClose, open = true }) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button onClick={onClose} aria-label="Close dialog">×</button>
        </div>
        {children}
      </Card>
    </div>
  );
}

export function ConfirmDialog({ title = 'Are you sure?', message, confirmLabel = 'Confirm', busy = false, onConfirm, onCancel }) {
  return (
    <Modal title={title} onClose={onCancel}>
      <div className="form">
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{message}</p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button variant="danger" disabled={busy} onClick={onConfirm}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function PageHeader({ eyebrow, title, subtitle, action }) {
  return (
    <div className="section-title">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Spinner() {
  return <span className="spinner" aria-label="Loading" />;
}

export function Loading({ label = 'Loading…' }) {
  return <div className="page-loading"><Spinner /> {label}</div>;
}

export function Skeleton({ height = 16, width = '100%', style }) {
  return <span className="skeleton" style={{ height, width, ...style }} aria-hidden="true" />;
}

export function PageSkeleton() {
  return (
    <>
      <div className="skeleton-grid">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={96} />)}
      </div>
      <Skeleton height={260} style={{ marginBottom: 16 }} />
      <Skeleton height={160} />
    </>
  );
}

export function ErrorState({ message }) {
  return <div className="page-error">{message}</div>;
}

export function EmptyState({ icon, title, action, children }) {
  return (
    <div className="empty">
      {icon && <span className="empty-icon">{icon}</span>}
      {title && <b>{title}</b>}
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Metric({ label, value, hint }) {
  return (
    <Card className="metric">
      <p>{label}</p>
      <h2>{value}</h2>
      <small>{hint}</small>
    </Card>
  );
}

export function Bar({ label, value }) {
  return (
    <div className="bar">
      <div><span>{label}</span><b>{value}%</b></div>
      <i><em style={{ width: `${value}%` }} /></i>
    </div>
  );
}
